import { useEffect, useMemo, useState } from "react";

import {
  PalAutoComplete,
  PalButton,
  PalDialog,
  PalIconButton,
  PalMenu,
  PalMenuItem,
  PalNumberFormat,
  PalTextField,
  PalTooltip,
  PalTypography,
} from "@palamar/fe-library";
import { withCardon } from "cardon";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import * as Yup from "yup";

import styled from "@emotion/styled";
import { Add, Delete, Edit, MoreVert } from "@mui/icons-material";
import { Alert, Box, FormHelperText, Grid, ListItemIcon, ListItemText } from "@mui/material";

import useProjectChange from "~common/hooks/useProjectChange";
import PRButton from "~components/Generic/PRButton";
import PRContainer from "~components/Generic/PRContainer";
import PRPage from "~components/Generic/PRPage";
import {
  billSettingsCurrencyCode,
  billSettingsCurrencyCodeMap,
  billSettingsCurrencyCodeOptions,
} from "~constants/billingAndInvoicing";
import DialogHelper from "~helpers/DialogHelper";
import HistoryHelper from "~helpers/HistoryHelper";
import {
  createOrUpdateBillSettings,
  getBillSettingItem,
  getBillSettingList,
  setBillSettingItem,
} from "~store/billingAndInvoicing/actions";
import { selectBillSettingItem, selectBillSettingList } from "~store/billingAndInvoicing/selectors";
import { selectCurrentProject } from "~store/user/selectors";

const BillingSettingsAddEditModal = withCardon(
  function BillingSettingsAddEditModalContent({ item, get }) {
    const { t } = useTranslation();
    const currentProject = useSelector(selectCurrentProject);
    const dispatch = useDispatch();

    const isEdit = !!item?.id || !!item?.item_name;
    const formik = useFormik({
      enableReinitialize: true,
      initialValues: {
        item_name: item?.item_name || "",
        item_unit_price: item?.item_unit_price || "",
      },
      validationSchema: Yup.object({
        item_name: Yup.string().required(t("common.required")),
        item_unit_price: Yup.number().required(t("common.required")),
      }),
      onSubmit: async (values) => {
        get(values)();
      },
    });

    const handleClose = () => {
      get(false)();
    };

    const handleCancel = () => {
      get(false)();
    };

    return (
      <PalDialog fullWidth maxWidth="xs" open={true} scroll="body" onClose={handleClose}>
        <PalDialog.Title>
          {isEdit ? t("component.billingSettingsAddEditModal.edit") : t("component.billingSettingsAddEditModal.create")}
        </PalDialog.Title>
        <PalDialog.Content dividers={false}>
          <Grid container mt={0.5} spacing={2}>
            <Grid item xs={12}>
              <PalTextField
                fullWidth
                invalid={formik.touched.item_name && formik.errors.item_name}
                label={t("component.billingSettingsAddEditModal.itemName")}
                name="item_name"
                value={formik.values.item_name}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <PalNumberFormat
                fullWidth
                invalid={formik.touched.item_unit_price && formik.errors.item_unit_price}
                label={t("component.billingSettingsAddEditModal.itemUnitPrice")}
                name="item_unit_price"
                NumericFormatProps={{
                  decimalScale: 2,
                  fixedDecimalScale: true,
                  allowNegative: false,
                  decimalSeparator: ".",
                  thousandSeparator: ",",
                  prefix: `${billSettingsCurrencyCodeMap[currentProject?.currency_code] || billSettingsCurrencyCode.USD} `,
                }}
                value={formik.values.item_unit_price}
                onChange={formik.handleChange}
              />
            </Grid>
          </Grid>
        </PalDialog.Content>

        <PalDialog.Actions>
          <PalButton color="error" variant="contained" onClick={formik.handleSubmit}>
            {isEdit ? t("common.update") : t("common.submit")}
          </PalButton>
          <PalButton onClick={handleCancel}>{t("common.cancel")}</PalButton>
        </PalDialog.Actions>
      </PalDialog>
    );
  },
  { destroyOnHide: true }
);

const StyledTd = styled.td`
  padding: 8px;
  border-bottom: 1px solid ${(props) => props.theme.palette.grey[200]};
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledTh = styled.th`
  padding: 8px;
  text-align: left;
  border-bottom: 1px solid ${(props) => props.theme.palette.grey[200]};
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BillSettingItem = ({ item, onEdit, onDelete, currencyCode }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickEdit = () => {
    handleClose();
    onEdit(item);
  };
  const handleClickDelete = () => {
    handleClose();
    onDelete(item);
  };

  return (
    <tr>
      <StyledTd>
        <PalTooltip title={item.item_name}>
          <span>{item.item_name}</span>
        </PalTooltip>
      </StyledTd>
      <StyledTd>
        <PalTooltip title={item.item_unit_price}>
          <span>
            <PalNumberFormat
              render
              NumericFormatProps={{
                decimalScale: 2,
                fixedDecimalScale: true,
                allowNegative: false,
                decimalSeparator: ".",
                thousandSeparator: ",",
                prefix: `${billSettingsCurrencyCodeMap[currencyCode]} `,
              }}
              value={item.item_unit_price}
            />
          </span>
        </PalTooltip>
      </StyledTd>
      <StyledTd>
        <Box display="flex" justifyContent="flex-end">
          <PalTooltip title={t("common.actions")}>
            <span>
              <PalIconButton onClick={handleMenuOpen}>
                <MoreVert />
              </PalIconButton>
            </span>
          </PalTooltip>
          <PalMenu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
            <PalMenuItem onClick={handleClickEdit}>
              <ListItemIcon>
                <Edit />
              </ListItemIcon>
              <ListItemText>{t("common.edit")}</ListItemText>
            </PalMenuItem>
            <PalMenuItem onClick={handleClickDelete}>
              <ListItemIcon
                sx={{
                  color: (theme) => theme.palette.error.main,
                }}
              >
                <Delete />
              </ListItemIcon>
              <ListItemText>{t("common.delete")}</ListItemText>
            </PalMenuItem>
          </PalMenu>
        </Box>
      </StyledTd>
    </tr>
  );
};
const DraftChargesEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const dispatch = useDispatch();

  const currentProject = useSelector(selectCurrentProject);

  const billSettingItem = useSelector(selectBillSettingItem);
  const billSettingList = useSelector(selectBillSettingList);

  useEffect(() => {
    dispatch(getBillSettingList(currentProject.id));
  }, [dispatch, currentProject.id]);

  useEffect(() => {
    if (!id) return;
    dispatch(getBillSettingItem(currentProject.id, id));
    return () => {
      dispatch(setBillSettingItem(null));
    };
  }, [dispatch, currentProject.id, id]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: billSettingItem?.id || "",
      currency_code: billSettingItem?.currency_code || "",
      items: billSettingItem?.items || [],
    },
    validationSchema: Yup.object({
      items: Yup.array().min(1, t("dashboard.draftChargesEdit.itemsRequired")),
    }),
    onSubmit: async (values) => {
      await dispatch(
        createOrUpdateBillSettings(currentProject.id, {
          ...values,
        })
      );
      handleClickCancel();
    },
  });

  const isEdit = !!billSettingItem?.id;

  useProjectChange(() => {
    HistoryHelper.push("/billing-and-invoicing/billing-catalog", {
      scope: "dashboard",
    });
  }, []);

  const parentName = useMemo(
    () => [
      {
        label: t("layout.sidebar.billingCatalog"),
        url: "/billing-and-invoicing/billing-catalog",
      },
      {
        label: id ? t("dashboard.draftChargesEdit.edit") : t("dashboard.draftChargesEdit.create"),
      },
    ],
    [id, t]
  );
  const handleClickCancel = () => {
    HistoryHelper.goBack("/billing-and-invoicing/billing-catalog/", { scope: "dashboard" });
  };

  const handleChangeBillSettingsCurrencyCode = (e, value) => {
    formik.setFieldValue("currency_code", value);
  };

  const handleClickAddNewItem = async () => {
    const result = await BillingSettingsAddEditModal.show();
    if (!result) return;
    formik.setFieldValue("items", [...formik.values.items, { ...result }]);
  };
  const currencyCodeOptions = useMemo(() => {
    const usedCurrencyCodes = billSettingList.map((item) => item.currency_code);
    const availableCurrencyCodes = billSettingsCurrencyCodeOptions.filter(
      (item) => !usedCurrencyCodes.includes(item.value) || (isEdit ? item.value === formik.values.currency_code : false)
    );
    return availableCurrencyCodes;
  }, [billSettingList, formik.values.currency_code, isEdit]);

  //set first of available currency code to formik
  useEffect(() => {
    if (
      currencyCodeOptions?.length > 0 &&
      !currencyCodeOptions.some((item) => item.value === formik.values.currency_code)
    ) {
      //!formik.values.currency_code) {
      formik.setFieldValue("currency_code", currencyCodeOptions[0].value);
    }
  }, [currencyCodeOptions, formik]);

  const showCurrencyCodeWarning = useMemo(() => {
    return currencyCodeOptions?.length === 0 && !isEdit;
  }, [isEdit, currencyCodeOptions]);
  return (
    <PRContainer name={t("layout.sidebar.billingAndInvoicing")} parentName={parentName}>
      <PRPage>
        {showCurrencyCodeWarning && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t("dashboard.draftChargesEdit.currencyCodeWarning")}
            </Alert>
          </>
        )}
        <Grid
          container
          spacing={2}
          width={{
            xs: 1,
            sm: 1,
            md: 1 / 2,
          }}
        >
          <Grid item xs={12}>
            <PalAutoComplete
              key={formik.values.currency_code}
              isPrimitiveValue
              disabled={!!id || showCurrencyCodeWarning}
              isClearable={false}
              label={t("dashboard.draftChargesEdit.currencyCodeType")}
              options={currencyCodeOptions}
              value={formik.values.currency_code}
              onChange={handleChangeBillSettingsCurrencyCode}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box display={"flex"} justifyContent={"flex-end"}>
              <PalButton disabled={!formik.values.currency_code} startIcon={<Add />} onClick={handleClickAddNewItem}>
                {t("common.add")}
              </PalButton>
            </Box>

            <Box
              sx={{
                borderRadius: "4px",
                padding: "16px",
                marginTop: "16px",
              }}
            >
              {!!formik.values?.items?.length && (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <Box component="tr" sx={{ background: (theme) => theme.palette.grey[100] }}>
                      <StyledTh>{t("component.billSettingsItem.itemName")}</StyledTh>
                      <StyledTh>{t("component.billSettingsItem.itemUnitPrice")}</StyledTh>
                      <StyledTh></StyledTh>
                    </Box>
                  </thead>
                  <tbody>
                    {formik.values.items.map((item, index) => (
                      <BillSettingItem
                        key={index}
                        currencyCode={formik.values.currency_code}
                        item={item}
                        onDelete={async (item) => {
                          if (!(await DialogHelper.showQuestionDeleteWithFormat(item.item_name))) return;
                          formik.setFieldValue(
                            "items",
                            formik.values.items.filter((_, i) => i !== index)
                          );
                        }}
                        onEdit={async (item) => {
                          const result = await BillingSettingsAddEditModal.show({ item });
                          if (!result) return;
                          formik.setFieldValue("items", [
                            ...formik.values.items.slice(0, index),
                            { ...result },
                            ...formik.values.items.slice(index + 1),
                          ]);
                        }}
                      />
                    ))}
                  </tbody>
                </table>
              )}
              {!formik.values?.items?.length && (
                <>
                  <Box
                    sx={{
                      minHeight: "100px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PalTypography color="text.secondary">{t("common.noItem")}</PalTypography>
                  </Box>
                  {formik.touched.items && formik.errors.items && (
                    <FormHelperText error={true}>{formik.errors.items}</FormHelperText>
                  )}
                </>
              )}
            </Box>
          </Grid>
        </Grid>

        <Grid container mt={2}>
          <Grid item ml="auto">
            <PRButton outline className="me-2" onClick={handleClickCancel}>
              {t("common.cancel")}
            </PRButton>
            <PRButton disabled={showCurrencyCodeWarning || !formik.values.currency_code} onClick={formik.handleSubmit}>
              {id ? t("common.update") : t("common.create")}
            </PRButton>
          </Grid>
        </Grid>
      </PRPage>
    </PRContainer>
  );
};

export default DraftChargesEdit;
